/**
 * TOCSidebar — Collapsible table-of-contents sidebar for the SBA document viewer.
 *
 * Renders the section tree indented by level with clickable entries that
 * navigate to the corresponding page in the viewer.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronDown, BookOpen } from 'lucide-react';
import type { TOCEntry } from '../../types/viewer';
import {
  isReservedSectionNumber,
  sectionDisplayNumber,
  seedCollapsed,
} from './tocHelpers';

interface TOCSidebarProps {
  entries: TOCEntry[];
  /** Called when user clicks a section — receives the full entry so the
   *  viewer can refine to the heading position using text-span search. */
  onNavigate: (entry: TOCEntry) => void;
  /** Currently visible page number (for highlighting active section) */
  currentPage: number;
  /** jp_pdf hover-driven override: the section the user is hovering, resolved
   *  by the viewer from the block's (page, y0). When non-null it wins over the
   *  page-based active section (two sections can share a page, so page-level
   *  highlighting can't disambiguate). Null/undefined → page-based fallback. */
  activeSectionOverride?: number | null;
}

const TOCSidebar: React.FC<TOCSidebarProps> = ({
  entries,
  onNavigate,
  currentPage,
  activeSectionOverride,
}) => {
  // Collapse state keyed by section row ID (NOT section_number) so
  // that two rows sharing the same section_number — e.g. the two
  // ``4.3`` rows in the Wegovy Clinical Pharmacology Review —
  // each have an independent collapse state.  Ancestor lookups
  // walk via ``parent_section_id`` below for the same reason.
  //
  // Seeded depth-relatively via ``seedCollapsed`` (not a hardcoded
  // ``level === 1``): a bundle tree's containers sit at level 0, so the
  // collapsed set must track whatever the top-most parent depth actually
  // is for THIS document's tree.
  const [collapsed, setCollapsed] = useState<Set<number>>(() => seedCollapsed(entries));

  // Re-seed when a new TOC/document arrives — the useState initializer
  // above only runs once per component instance, but the SBA/EU viewers
  // reuse one mounted TOCSidebar across document navigation, so without
  // this effect the second document would inherit the first document's
  // stale collapsed-row-ID set (wrong rows collapsed, or none at all).
  useEffect(() => {
    setCollapsed(seedCollapsed(entries));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  // Group collapse keyed by prefix/group_key string — unrelated to
  // section rows, so a separate set is clearer than union-typing the
  // section-ID set with group-prefix strings.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    // EU SmPC bundles carry an explicit ``group_key`` per presentation.
    // Collapse every presentation except the first so a 7-strength
    // bundle (GONAL-f) opens as 7 tidy banners instead of ~200 flat
    // rows.  SBA review-prefix groups have no ``group_key`` → untouched.
    const keys: string[] = [];
    for (const e of entries) {
      if (e.group_key && !keys.includes(e.group_key)) keys.push(e.group_key);
    }
    return new Set<string>(keys.slice(1));
  });

  const toggleCollapse = useCallback((id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((prefix: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  }, []);

  // Index rows by ID once for O(1) ancestor walks below.
  const entriesById = React.useMemo(() => {
    const map = new Map<number, TOCEntry>();
    for (const e of entries) map.set(e.id, e);
    return map;
  }, [entries]);

  // Determine which section is "active": a hover-resolved override (jp_pdf)
  // wins; otherwise fall back to the page-based scroll-spy.
  const activeSectionId =
    activeSectionOverride != null
      ? activeSectionOverride
      : findActiveSection(entries, currentPage);

  return (
    <nav className="h-full overflow-y-auto py-3 px-2" aria-label="Table of contents">
      <div className="flex items-center gap-1.5 px-2 pb-2 mb-2 border-b border-gray-200">
        <BookOpen className="w-4 h-4 text-indigo-600" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Contents
        </span>
      </div>

      <ul className="space-y-0.5">
        {entries.map((entry, idx) => {
          // "Has children" = any entry points at THIS row by
          // parent_section_id (not by section_number — two rows
          // sharing a section_number would otherwise both show
          // a collapse triangle for the children of whichever one
          // they actually belong to).
          const isParent = entries.some(
            (e) => e.parent_section_id === entry.id,
          );
          const isCollapsed = collapsed.has(entry.id);
          const isHidden =
            entry.parent_section_id !== null &&
            isAncestorCollapsed(entry, entriesById, collapsed);
          const isActive = entry.id === activeSectionId;

          // Detect group change.  Two group sources, mutually
          // exclusive per family: SBA multi-PDF uses a review-type
          // prefix embedded in section_number; EU SmPC bundles carry an
          // explicit ``group_key`` per presentation.  ``group_label``
          // (EU) gives a human banner; SBA derives one from the prefix.
          const currentPrefix =
            entry.group_key ?? extractReviewPrefix(entry.section_number);
          const prevEntry = idx > 0 ? entries[idx - 1] : undefined;
          const prevPrefix = prevEntry
            ? (prevEntry.group_key ?? extractReviewPrefix(prevEntry.section_number))
            : null;
          const showGroupHeader = currentPrefix && currentPrefix !== prevPrefix;
          const isGroupCollapsed = currentPrefix ? collapsedGroups.has(currentPrefix) : false;
          const groupLabel = entry.group_label
            ?? (currentPrefix ? currentPrefix.replace(/_/g, ' ') : '');

          // When the group is collapsed, hide every entry in the group
          // EXCEPT the one that owns the group header button — that one
          // still needs to render so the user has something to click to
          // re-expand the group. Previously this early-return also
          // killed the header button itself, so once a group was
          // collapsed the only way back was a page refresh.
          if (isGroupCollapsed && currentPrefix && !showGroupHeader) return null;

          if (isHidden && !showGroupHeader) return null;

          return (
            <React.Fragment key={entry.id}>
              {/* Group header: SBA multi-PDF review type (short, UPPER)
                  or EU SmPC presentation spec (longer, mixed-case — let
                  it wrap rather than truncate the strength). */}
              {showGroupHeader && currentPrefix && (
                <li className="mt-3 first:mt-0">
                  <button
                    className={`w-full text-left flex items-start gap-1.5 px-2 py-1.5 bg-indigo-50 rounded text-xs font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors ${
                      entry.group_label ? '' : 'uppercase tracking-wide items-center'
                    }`}
                    onClick={() => toggleGroup(currentPrefix)}
                  >
                    {isGroupCollapsed ? (
                      <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="leading-snug">{groupLabel}</span>
                    {entry.page_start !== null && (
                      <span className="ml-auto pl-1 text-[10px] text-indigo-400 font-normal normal-case flex-shrink-0">
                        p.{entry.page_start}
                      </span>
                    )}
                  </button>
                </li>
              )}

              {!isHidden && !isGroupCollapsed && !entry.is_group_header && (
                <li>
                  <button
                    className={`w-full text-left flex items-start gap-1 px-2 py-1 rounded text-sm transition-colors ${
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    style={{ paddingLeft: `${(entry.level - 1) * 12 + (currentPrefix ? 20 : 8)}px` }}
                    onClick={() => {
                      if (entry.page_start !== null) {
                        onNavigate(entry);
                      }
                    }}
                    title={
                      entry.section_number
                        && !isReservedSectionNumber(entry.section_number)
                        ? `${entry.section_number} ${entry.title}`
                        : entry.title
                    }
                  >
                    {isParent ? (
                      <span
                        className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-gray-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapse(entry.id);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            toggleCollapse(entry.id);
                          }
                        }}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </span>
                    ) : (
                      <span className="w-3.5 flex-shrink-0" />
                    )}

                    <span className="leading-snug">
                      <span className="text-gray-400 mr-1 text-xs">
                        {sectionDisplayNumber(entry)}
                      </span>
                      {extractDisplayTitle(entry.section_number, entry.title)}
                    </span>
                  </button>
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ul>
    </nav>
  );
};

/**
 * Find the section ID that best matches the current page number.
 * Walks sections from last to first, returning the first section
 * whose page_start <= currentPage.
 */
function findActiveSection(
  entries: TOCEntry[],
  currentPage: number,
): number | null {
  let best: TOCEntry | null = null;
  for (const entry of entries) {
    if (
      entry.page_start !== null &&
      entry.page_start <= currentPage &&
      (best === null ||
        best.page_start === null ||
        entry.page_start > best.page_start)
    ) {
      best = entry;
    }
  }
  return best?.id ?? null;
}

/**
 * Check if any ancestor of the entry is collapsed.
 *
 * Walks the parent chain via ``parent_section_id`` (backend-
 * resolved) rather than ``parent_section_number``, so two rows
 * sharing a section_number don't both appear collapsed when
 * only one of them is toggled.  ``entriesById`` is passed in
 * pre-built so every walk is O(depth) without a per-step scan
 * of the full entry list.
 */
function isAncestorCollapsed(
  entry: TOCEntry,
  entriesById: Map<number, TOCEntry>,
  collapsedSet: Set<number>,
): boolean {
  let parentId = entry.parent_section_id;
  const seen = new Set<number>();
  while (parentId !== null) {
    if (seen.has(parentId)) return false; // cycle guard
    seen.add(parentId);
    if (collapsedSet.has(parentId)) return true;
    const parent = entriesById.get(parentId);
    parentId = parent?.parent_section_id ?? null;
  }
  return false;
}

/**
 * Extract a display-friendly title.
 * If title is empty/generic, derive from the review type prefix.
 * e.g., section_number "Clinical_Pharmacology_Review_3.2" with empty title
 *       → "Clinical Pharmacology Review"
 */
function extractDisplayTitle(
  sectionNumber: string | null,
  title: string,
): string {
  if (title) return title;
  if (!sectionNumber) return '';
  // Derive from prefix: "Clinical_Pharmacology_Review_3.2" → "Clinical Pharmacology Review"
  const prefixMatch = sectionNumber.match(/^(.+?)_(\d+(?:\.\d+)*)$/);
  if (prefixMatch?.[1]) {
    return prefixMatch[1].replace(/_/g, ' ');
  }
  return sectionNumber;
}

/**
 * Extract the review type prefix from a prefixed section number.
 * e.g., "Clinical_Pharmacology_Review_3.2.1" → "Clinical_Pharmacology_Review"
 *       "Medical_Review_1" → "Medical_Review"
 *       "5.3" → null (no prefix, single-PDF document)
 */
function extractReviewPrefix(sectionNumber: string | null): string | null {
  if (!sectionNumber) return null;
  const match = sectionNumber.match(/^(.+?)_(\d+(?:\.\d+)*)$/);
  return match?.[1] ?? null;
}

export default TOCSidebar;
